import React from 'react';
import {
    Box,
    Typography,
    Avatar,
    Rating,
    Chip,
    Skeleton,
    Alert,
    Divider,
} from '@mui/material';
import {
    Verified as VerifiedIcon,
    Star as StarIcon,
    Language as LanguageIcon,
    LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useHostProfile, PublicHostProfile } from '@/hooks/api/useHostProfile';

interface HostProfileCardProps {
    hostId: string;
    compact?: boolean;
}

export const HostProfileCard: React.FC<HostProfileCardProps> = ({
    hostId,
    compact = false,
}) => {
    const { data: hostProfile, isLoading, error } = useHostProfile(hostId);

    // Loading state
    if (isLoading) {
        return (
            <Box sx={{ p: 3, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Skeleton variant="circular" width={64} height={64} />
                    <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="60%" height={28} />
                        <Skeleton variant="text" width="40%" height={20} />
                    </Box>
                </Box>
                {!compact && (
                    <>
                        <Skeleton variant="text" width="100%" height={20} />
                        <Skeleton variant="text" width="80%" height={20} />
                        <Skeleton variant="text" width="90%" height={20} />
                    </>
                )}
            </Box>
        );
    }

    // Error state
    if (error || !hostProfile) {
        return (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
                Host information not available
            </Alert>
        );
    }

    const getJoinedYear = () => {
        try {
            return new Date(hostProfile.joinedDate).getFullYear();
        } catch {
            return 'Recently';
        }
    };

    const getLocationText = () => {
        const { location } = hostProfile;
        if (!location) return null;

        const parts = [location.city, location.state, location.country].filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : null;
    };

    return (
        <Box sx={{ p: 3, border: 1, borderColor: 'divider', borderRadius: 2 }}>
            {/* Host Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: compact ? 0 : 2 }}>
                <Avatar
                    src={hostProfile.profilePicture}
                    sx={{
                        width: compact ? 48 : 64,
                        height: compact ? 48 : 64,
                        fontSize: compact ? '1.2rem' : '1.5rem',
                        fontWeight: 'bold',
                        bgcolor: 'primary.main',
                    }}
                >
                    {hostProfile.name.charAt(0)}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant={compact ? 'subtitle1' : 'h6'} fontWeight="bold" noWrap>
                            {hostProfile.name}
                        </Typography>
                        {hostProfile.isSuperhost && (
                            <Chip
                                icon={<StarIcon sx={{ fontSize: 16 }} />}
                                label="Superhost"
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        )}
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                        Host since {getJoinedYear()}
                    </Typography>

                    {hostProfile.verificationLevel && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <VerifiedIcon sx={{ fontSize: 16, color: 'success.main' }} />
                            <Typography variant="body2" color="success.main">
                                {hostProfile.verificationLevel}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {!compact && (
                <>
                    {/* Host Stats */}
                    {(hostProfile.averageRating || hostProfile.totalReviews || hostProfile.responseRate) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                            {hostProfile.averageRating && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Rating
                                        value={hostProfile.averageRating}
                                        precision={0.1}
                                        size="small"
                                        readOnly
                                    />
                                    <Typography variant="body2" fontWeight={600}>
                                        {hostProfile.averageRating.toFixed(1)}
                                    </Typography>
                                </Box>
                            )}

                            {hostProfile.totalReviews && hostProfile.totalReviews > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    • {hostProfile.totalReviews} review{hostProfile.totalReviews !== 1 ? 's' : ''}
                                </Typography>
                            )}

                            {hostProfile.responseRate && (
                                <Typography variant="body2" color="text.secondary">
                                    • {Math.round(hostProfile.responseRate * 100)}% response rate
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Response Time */}
                    {hostProfile.responseTime && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Responds within {hostProfile.responseTime}
                        </Typography>
                    )}

                    {/* Bio */}
                    {hostProfile.bio && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                {hostProfile.bio}
                            </Typography>
                        </>
                    )}

                    {/* Additional Info */}
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {/* Languages */}
                        {hostProfile.languages && hostProfile.languages.length > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LanguageIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                    Speaks {hostProfile.languages.join(', ')}
                                </Typography>
                            </Box>
                        )}

                        {/* Location */}
                        {getLocationText() && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                    Lives in {getLocationText()}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </>
            )}
        </Box>
    );
};

export default HostProfileCard;